import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../Axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import useAuth from "../contexts/UserContext.tsx";
import { PulseLoader } from "react-spinners";

export default function MathlabUsers() {
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSchoolType, setSelectedSchoolType] = useState("");
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [schools, setSchools] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getSchools = useCallback(
    async (selectedDistrict, schoolType) => {
      if (selectedSchoolType === "") {
        toast.info("Please select a school type");
        return;
      }
      axiosInstance
        .get(
          `getSchools?district='${selectedDistrict}'&school_type='${schoolType}'&auth_token=${user.auth_token}`
        )
        .then((res) => {
          if (res.data.data.message === "Invalid User") {
            toast.error("Invalid User");
            logout();
            return;
          }
          if (res.data.data.status === false) {
            toast.error(
              "No schools found for the selected district and school type"
            );
            return;
          }
          toast.success("Schools fetched successfully");
          setSchools(res.data.data.data);
        })
        .catch((err) => {
          toast.error(err);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSchoolType, user.auth_token]
  );

  const getUsers = async (schoolId) => {
    setLoading(true);
    axiosInstance
      .get(
        `getMathLabUsers?school_id=${schoolId}&auth_token=${user.auth_token}`
      )
      .then((res) => {
        setStudentsData(res.data.data.data);
        if (res.data.data.data === undefined) {
          toast.error("No users found for the selected school");
          return;
        }
        toast.success("Users fetched successfully");
        sessionStorage.setItem("curr_school_id", schoolId);
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNewUser = () => {
    const school_id = sessionStorage.getItem("curr_school_id");
    if (school_id === null) {
      toast.error("Please select a school to add a new user");
      return;
    } else {
      navigate(`/add-mathlab-user`);
    }
  };

  useEffect(() => {
    if (user.user_type !== "admin") {
      return navigate("/");
    }
    setDistricts(user.view_access_list.district.split(","));
    setSchoolTypes(user.view_access_list.school_types.split(","));
  }, [navigate, user.user_type, user.view_access_list]);

  useEffect(() => {
    if (selectedDistrict !== "") {
      getSchools(selectedDistrict, selectedSchoolType);
    }
  }, [selectedDistrict, selectedSchoolType, getSchools]);

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="card">
            <div className="card-header">
              <h3>
                <b>Mathlab Users</b>
              </h3>
            </div>
            {/* Add Filter based on District, school_type and school */}
            <div className="card-header">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <select
                      className="form-control"
                      id="district"
                      onChange={(e) => setSelectedDistrict(e.target.value)}
                    >
                      <option value="">Select District</option>
                      {districts.sort().map((district, index) => {
                        return (
                          <option key={index} value={district}>
                            {district}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <select
                      className="form-control"
                      id="schoolType"
                      onChange={(e) => setSelectedSchoolType(e.target.value)}
                    >
                      <option value="">Select School Type</option>
                      {schoolTypes.sort().map((schoolType, index) => {
                        return (
                          <option key={index} value={schoolType}>
                            {schoolType}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <select
                      className="form-control"
                      id="school"
                      onChange={(e) => getUsers(e.target.value)}
                    >
                      <option value="">Select School</option>
                      {schools.sort().map((school, index) => {
                        return (
                          <option key={index} value={school.id}>
                            {school.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-[30rem] overflow-scroll">
              <table className="table table-striped h-20 max-h-80 overflow-scroll">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Roll Number</th>
                    <th>Current Grade</th>
                    <th>Cell Number</th>
                    <th>User Name</th>
                  </tr>
                </thead>
                <tbody id="userList">
                  {/* User data will be dynamically inserted here */}
                  {studentsData && studentsData.length === 0 && (
                    <tr>
                      <td colSpan="6">
                        {loading && (
                          <div className="flex justify-center items-center h-[50vh]">
                            <PulseLoader
                              color="#3B82F6"
                              loading={loading}
                              size={15}
                            />
                          </div>
                        )}
                        {!loading && (
                          <div
                            className="alert flex justify-center"
                            role="alert"
                          >
                            Select District, School Type and School to view
                            users
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {studentsData &&
                    studentsData.map((student, index) => {
                      return (
                        <tr key={index}>
                          <td>{student.fname}</td>
                          <td>{student.lname}</td>
                          <td>{student.roll_number}</td>
                          <td>{student.current_grade}</td>
                          <td>{student.cell_num}</td>
                          <td>{student.user_name}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-2">
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#4CAF50",
            color: "white",
            "&:hover": {
              backgroundColor: "#45a049",
            },
          }}
          onClick={handleNewUser}
        >
          Add new user
        </Button>
      </div>
    </div>
  );
}
